import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Checkbox } from '@overdose/components'
import { InfoWindow, MarkerClustererF, MarkerF } from '@react-google-maps/api'
import classNames from 'classnames'
import dynamic from 'next/dynamic'
import { useRouter } from 'next/router'
import { Button } from '~/components/Button'
import { SectionContainer } from '~/components/SectionContainer'
import Select from '~/components/Select/Select'
import { StoreMapItemInfoWindow } from '~/components/StoreMapItemTooltip/StoreMapItemTooltip'
import StoreSearchForm from '~/components/StoreSearchForm/StoreSearchForm'
import StoresList from '~/components/StoresList/StoresList'
import {
  StoresMapClusterIconStyles,
  StoresMapMarkerIconStyle,
  StoresMapRtmClusterIconStyles,
  StoresMapRtmMarkerIconStyle,
  StoresResellerMapMarkerIconStyle,
} from '~/components/StoresMap'
import Typography, {
  TypographyTag,
  TypographyVariant,
} from '~/components/Typography'
import {
  usePage,
  useUserLocation,
  useSession,
  useSearchPlace,
  useUserDeliveryPostcode,
  useUserPreferredStore,
} from '~/hooks'
import { Coordinates, StoreSummary } from '~/types'
import { DEFAULT_COUNTRY_CODE_AU } from '~/constants/countries'
import { Client } from '~/customClients/client'

import { StoreLocatorMapProps } from '~/sections/StoreLocatorMap/StoreLocatorMap.types'
import styles from './StoreLocatorMap.module.css'

const StoresMap = dynamic(
  async () => {
    const mod = await import('~/components/StoresMap')
    return mod.StoresMap
  },
  { ssr: false }
)

const defaultZoom = 4
const storeDetailZoom = 12
const yAxisRatio = 11
const reduceZoomSize = 0.5

const defaultCenterAU = {
  latitude: -26.9732951,
  longitude: 134.170855,
}
const defaultCenterNZ = {
  latitude: -40.9006,
  longitude: 174.886,
}

const powNumber = 2
const defaultRegion = {
  label: 'All States',
  value: 'all',
  url: '/store-finder',
}

const MAX_DISTANCE = 10
const MAX_RESULT = 10

export const StoreLocatorMap = ({
  regions,
  stores,
  currentRegion,
  map,
  width = 'full',
}: StoreLocatorMapProps) => {
  const router = useRouter()
  const { countryCode, brand } = usePage()
  const [storeMap, setStoreMap] = useState<google.maps.Map | null>(null)
  const [mapBounds, setMapBounds] = useState<google.maps.LatLngBounds | null>(
    null
  )
  const [filteredStores, setFilteredStores] =
    useState<Array<StoreSummary>>(stores)
  const [selectedStore, setSelectedStore] = useState<StoreSummary>(null)
  const [isShowList, setIsShowList] = useState(false)
  const { geolocateWithPermission } = useUserLocation()
  const { session } = useSession()
  const { searchPlaceDetails } = useSearchPlace()
  const statusRef = useRef('')
  const locationRef = useRef('')
  const inputStatusRef = useRef(false)
  const [initialData, setInitialData] = useState(null)
  const { postCodeDrawerIsOpen } = useUserDeliveryPostcode()
  const { preferredStoreIsOpen } = useUserPreferredStore()
  const [wasOpenedDrawer, setWasOpenedDrawer] = useState(false)
  const [hideResellers, setHideResellers] = useState(false)

  const handleSelectStore = useCallback(
    (store: StoreSummary) => {
      setSelectedStore(store)
      storeMap.setCenter({ lat: store.latitude, lng: store.longitude })
      storeMap.setZoom(storeDetailZoom)
    },
    [storeMap, setSelectedStore]
  )

  const handleChangeRegion = useCallback(
    (val: string) => {
      if (currentRegion?.value === val && storeMap && initialData) {
        storeMap.fitBounds(initialData?.bounds)
        storeMap.setZoom(initialData.zoom)
        return
      }

      const selectedRegion = regions.find((s) => {
        return s.value === val
      })

      router.replace(selectedRegion?.url || '/store-finder')
    },
    [router, storeMap, initialData, currentRegion, regions]
  )

  const handleChangedLocation = useCallback(
    (
      location: Coordinates,
      bounds?: google.maps.LatLngBounds,
      postalCode?: string
    ) => {
      if (!storeMap) {
        return
      }
      const nearestLngLat = {
        lat: location.latitude,
        lng: location.longitude,
      }
      if (location) {
        storeMap.setCenter({ lat: location.latitude, lng: location.longitude })
        const nearestStore = stores.reduce<{
          distance: number
          store: StoreSummary
        }>((nearStore, store) => {
          const distance =
            (location.latitude - store.latitude) ** powNumber +
            (location.longitude - store.longitude) ** powNumber
          if (!nearStore || nearStore?.distance > distance) {
            nearStore = {
              distance,
              store,
            }
          }
          return nearStore
        }, null)
        nearestLngLat.lat = nearestStore.store.latitude
        nearestLngLat.lng = nearestStore.store.longitude
      }

      const newBound = bounds || storeMap.getBounds()

      let searchStoreList: StoreSummary[] = []
      if (postalCode) {
        searchStoreList = stores?.filter((store) => {
          return store.address.postalCode === postalCode
        })
      }

      if (searchStoreList.length > 0) {
        const storeBounds = new google.maps.LatLngBounds()
        for (const newStore of searchStoreList) {
          storeBounds.extend(
            new google.maps.LatLng(newStore.latitude, newStore.longitude)
          )
        }

        setFilteredStores(searchStoreList)
        storeMap.fitBounds(storeBounds)
        return
      }

      if (newBound && !storeMap.getBounds().contains(nearestLngLat)) {
        if (newBound.contains(nearestLngLat)) {
          storeMap.fitBounds(newBound)
        } else {
          storeMap.fitBounds(newBound.extend(nearestLngLat))
        }

        storeMap.setZoom(storeMap.getZoom() - reduceZoomSize)
      }

      if (!initialData) {
        const topNearestStore = stores
          .map((store) => {
            const distance =
              (nearestLngLat.lat - store.latitude) ** powNumber +
              (nearestLngLat.lng - store.longitude) ** powNumber
            return { distance, store }
          })
          .filter(({ distance }) => {
            return distance <= MAX_DISTANCE
          })
          .sort((a, b) => {
            return a.distance - b.distance
          })
          .slice(0, MAX_RESULT)

        const storeBounds = new google.maps.LatLngBounds()
        for (const newStore of topNearestStore) {
          storeBounds.extend(
            new google.maps.LatLng(
              newStore.store.latitude,
              newStore.store.longitude
            )
          )
        }

        // Filter stores based on hideResellers state
        const filtered = hideResellers
          ? stores.filter((store) => {
              return !store.name.includes('RESELLER')
            })
          : stores

        setFilteredStores(filtered)
        storeMap.fitBounds(storeBounds)
      }
    },
    [storeMap, stores, hideResellers, initialData]
  )

  const getStoreVisible = useCallback(
    (store: StoreSummary, map?: google.maps.Map) => {
      const bounds = map?.getBounds?.()
      if (!bounds) {
        return true
      }

      if (!bounds.contains({ lat: store.latitude, lng: store.longitude })) {
        return false
      }

      const delta =
        (bounds.getNorthEast().lat() - bounds.getSouthWest().lat()) / yAxisRatio

      return bounds.contains({
        lat: store.latitude + delta,
        lng: store.longitude,
      })
    },
    []
  )

  const handleBoundsChanged = useCallback(
    (googleMap: google.maps.Map) => {
      const _bounds = googleMap.getBounds()

      if (!mapBounds) {
        setInitialData({ bounds: _bounds, zoom: googleMap.getZoom() })
      }

      if (_bounds.equals(mapBounds)) {
        return
      }
      // Filter stores based on hideResellers state
      const filtered = hideResellers
        ? stores.filter((store) => {
            return !store.name.includes('RESELLER')
          })
        : stores
      const _filtered = filtered
        .map((store, index) => {
          return { ...store, id: index + 1 }
        })
        .filter((store) => {
          return getStoreVisible(store, googleMap)
        })

      if (selectedStore && !getStoreVisible(selectedStore, googleMap)) {
        setSelectedStore(null)
      }

      setFilteredStores(_filtered || [])
      setMapBounds(_bounds)
    },
    [
      mapBounds,
      selectedStore,
      setSelectedStore,
      setFilteredStores,
      setMapBounds,
      stores,
      getStoreVisible,
      setInitialData,
    ]
  )

  const handleMapInstanceLoaded = useCallback(
    (googleMap: google.maps.Map) => {
      if (googleMap) {
        const setBound = new google.maps.LatLngBounds()
        // Filter stores based on hideResellers state
        const filtered = hideResellers
          ? stores.filter((store) => {
              return !store.name.includes('RESELLER')
            })
          : stores
        filtered.forEach((store) => {
          setBound?.extend(
            new google.maps.LatLng(store.latitude, store.longitude)
          )
        })
        setInitialData(null)
        googleMap?.fitBounds(setBound)
        setStoreMap(googleMap)
      }
    },
    [stores, setInitialData]
  )

  const handleChangedUserPostalCode = useCallback(
    async (postalCode: string, search: string) => {
      const place = await searchPlaceDetails(search)
      if (!place?.geometry?.location) {
        return
      }

      const location = {
        latitude: place.geometry?.location.lat(),
        longitude: place.geometry?.location.lng(),
      }

      handleChangedLocation(location, undefined, postalCode)
    },
    [searchPlaceDetails, handleChangedLocation]
  )

  const handleChangedUserStoreKey = useCallback(
    async (storeKey: string) => {
      if (!storeKey) {
        return
      }

      const preferredStore = stores?.find((s) => {
        return s.storeKey === storeKey
      })

      if (preferredStore) {
        handleSelectStore(preferredStore)
        return
      }

      const response = await Client.store.getStoreByStorekey(session, storeKey)
      if (!response.getErrorSafe()) {
        const stores = response.getValueSafe()
        if (!stores.length) {
          return
        }

        handleChangedLocation({
          latitude: Number(stores[0].latitude),
          longitude: Number(stores[0].longitude),
        })
      }
    },
    [session, stores, handleSelectStore, handleChangedLocation]
  )

  const handleFindStore = useCallback(
    (location: Coordinates, postalCode?: string) => {
      if (!currentRegion) {
        handleChangedLocation(location, null, postalCode)
        return
      }

      router.push(
        `/store-finder?postcode=${postalCode || ''}&location=${
          location.latitude
        },${location.longitude}`
      )
    },
    [currentRegion, router, handleChangedLocation]
  )

  const handleUpdateStoreMap = useCallback(async () => {
    if (!wasOpenedDrawer && currentRegion) {
      return
    }

    const { postcode, location } = router.query
    if (postcode || location) {
      if (!inputStatusRef.current) {
        const [latitude, longitude] = location.toString().split(',')
        handleChangedLocation(
          { latitude: Number(latitude), longitude: Number(longitude) },
          null,
          postcode?.toString()
        )
        inputStatusRef.current = true
      }
      return
    }

    const { storeKey, deliveryPostcode, deliveryCity } = session.token
    const oldStatus = (statusRef?.current || '').split('-')
    const newStatus = `${deliveryPostcode || ''}-${storeKey || ''}`

    let userLocation: Coordinates = null
    if (!wasOpenedDrawer && geolocateWithPermission) {
      userLocation = await geolocateWithPermission()
    }

    if (userLocation) {
      const strLocation = `${userLocation.latitude},${userLocation.longitude}`
      if (locationRef.current !== strLocation) {
        locationRef.current = strLocation
        const { latitude, longitude } = userLocation
        storeMap.setCenter({ lat: latitude, lng: longitude })
      }
    } else if (deliveryPostcode && oldStatus[0] !== deliveryPostcode) {
      handleChangedUserPostalCode(
        deliveryPostcode,
        `${deliveryCity || ''}, ${deliveryPostcode}`
      )
    } else if (storeKey && oldStatus[1] !== storeKey) {
      handleChangedUserStoreKey(storeKey)
    }

    statusRef.current = newStatus
  }, [
    session?.token,
    storeMap,
    router.query,
    wasOpenedDrawer,
    currentRegion,
    geolocateWithPermission,
    handleChangedUserPostalCode,
    handleChangedUserStoreKey,
    handleChangedLocation,
  ])

  const defaultCenter = useMemo(() => {
    if (countryCode === DEFAULT_COUNTRY_CODE_AU) {
      return defaultCenterAU
    }
    return defaultCenterNZ
  }, [countryCode])

  useEffect(() => {
    if (postCodeDrawerIsOpen || preferredStoreIsOpen) {
      setWasOpenedDrawer(true)
    }
  }, [postCodeDrawerIsOpen, preferredStoreIsOpen, setWasOpenedDrawer])

  useEffect(() => {
    if (!storeMap || !session?.token || !initialData) {
      return
    }
    handleUpdateStoreMap()
  }, [session?.token, storeMap, initialData, handleUpdateStoreMap])

  const getIconStyle = (style) => {
    return {
      ...style,
      size: new google.maps.Size(style.width, style.height),
      anchor: new google.maps.Point(style.width * 0.5, style.height * 0.5),
    }
  }

  return (
    <SectionContainer width={width}>
      <div className={styles.root}>
        <div
          className={classNames(
            styles.left,
            'text-secondary-muted px-4 lg:px-0 lg:pointer-events-none'
          )}>
          <div
            className={classNames(
              'flex flex-col lg:h-full pointer-events-auto'
            )}>
            <div className='mb-4'>
              <Typography
                tag={TypographyTag.p}
                variant={TypographyVariant.Heading5}>
                Store Finder
              </Typography>
            </div>
            <Typography
              tag={TypographyTag.p}
              variant={TypographyVariant.BodyRegularExtraLineHeight}
              className='tracking-normal'>
              {brand === 'rtm' ? 'Road Tech Marine' : 'Jaycar'} has over{' '}
              {stores?.length || 1} Stores & Resellers located throughout{' '}
              {countryCode === DEFAULT_COUNTRY_CODE_AU
                ? 'Australia'
                : 'New Zealand'}
              .
              <br />
              Use this form to search for a store
            </Typography>
            <div className='mt-4 4xl:mt-10'>
              <StoreSearchForm
                label='City or Postcode'
                defaultValue={router?.query?.postcode?.toString()}
                onLocate={handleFindStore}
              />
              <Checkbox
                label='Hide Reseller'
                name='hide_reseller'
                size='md'
                defaultChecked={false}
                theme={{
                  content: 'pt-2',
                  box: classNames(styles.customCheckbox),
                }}
                onChange={(value) => {
                  setHideResellers(value)
                  // Filter stores based on hideResellers state
                  const filtered = value
                    ? stores.filter((store) => {
                        return !store.name.includes('RESELLER')
                      })
                    : stores
                  setFilteredStores(filtered)
                }}
              />
            </div>
            <div className='hidden lg:block flex-1 mt-8 4xl:mt-10 overflow-y-auto'>
              <StoresList
                stores={filteredStores}
                onClickStore={handleSelectStore}
              />
            </div>
          </div>
        </div>
        <div
          className={classNames(
            'flex items-center text-center text-white mt-4 lg:hidden',
            styles.stateSelector
          )}>
          <Typography
            tag={TypographyTag.div}
            variant={TypographyVariant.BodyRegularBold}
            className='flex-1 p-2'>
            Select State
          </Typography>
          {!!regions?.length && (
            <div className='flex-1'>
              <Select
                theme={{
                  root: classNames(styles.select, 'w-fit font-extrabold !px-7'),
                  input: '!text-white !w-16 !leading-4 !tracking-body-regular',
                  list: styles.selectList,
                }}
                value={currentRegion?.value || 'all'}
                options={[defaultRegion, ...regions]}
                onChange={handleChangeRegion}
              />
            </div>
          )}
        </div>
        <div className='flex items-center lg:hidden'>
          <Button
            variant='ghost'
            theme={{
              root: classNames(
                styles.tab,
                isShowList ? styles.tabDisable : styles.tabEnable,
                'flex-1'
              ),
            }}
            onClick={() => {
              return setIsShowList(false)
            }}>
            Map
          </Button>
          <Button
            variant='ghost'
            theme={{
              root: classNames(
                styles.tab,
                isShowList ? styles.tabEnable : styles.tabDisable,
                'flex-1'
              ),
            }}
            onClick={() => {
              return setIsShowList(true)
            }}>
            List
          </Button>
        </div>
        <div
          className={classNames(
            styles.mapWrapper,
            isShowList ? 'hidden lg:block' : 'block',
            'relative lg:h-full lg:min-h-full flex-1'
          )}>
          <StoresMap
            center={map?.center || defaultCenter}
            initialZoom={map?.initialZoom || defaultZoom}
            onLoadedMap={handleMapInstanceLoaded}
            onBoundsChanged={handleBoundsChanged}>
            {storeMap && (
              <MarkerClustererF
                options={{
                  styles:
                    brand === 'rtm'
                      ? StoresMapRtmClusterIconStyles
                      : StoresMapClusterIconStyles,
                  minimumClusterSize: 3,
                  averageCenter: true,
                }}>
                {(clusterer) => {
                  const map = clusterer.getMap() as google.maps.Map
                  // Filter stores based on hideResellers state
                  const filtered = hideResellers
                    ? stores.filter((store) => {
                        return !store.name.includes('RESELLER')
                      })
                    : stores

                  return (
                    <>
                      {filtered.map((store, index) => {
                        const isVisible = getStoreVisible(store, map)
                        const storeName = store?.displayName || store?.name
                        const isReseller = storeName
                          .toLowerCase()
                          .includes('reseller')

                        let iconStyle
                        if (brand === 'rtm') {
                          iconStyle = getIconStyle(StoresMapRtmMarkerIconStyle)
                        } else if (isReseller) {
                          iconStyle = getIconStyle(
                            StoresResellerMapMarkerIconStyle
                          )
                        } else {
                          iconStyle = getIconStyle(StoresMapMarkerIconStyle)
                        }
                        const icon = iconStyle

                        return (
                          // @ts-ignore
                          <MarkerF
                            key={`${index}_${store.storeKey}`}
                            visible={isVisible}
                            position={{
                              lat: store.latitude,
                              lng: store.longitude,
                            }}
                            // noClustererRedraw
                            clusterer={clusterer}
                            label={{
                              text: String(index + 1),
                              color: brand === 'rtm' ? '#000' : '#fff',
                              fontFamily: '',
                              className:
                                'font-body-regular-font-family leading-4 font-extrabold tracking-body-regular',
                            }}
                            icon={icon}
                            onClick={() => {
                              return handleSelectStore(store)
                            }}
                          />
                        )
                      })}
                    </>
                  )
                }}
              </MarkerClustererF>
            )}
            {selectedStore && (
              <div>
                {/* @ts-ignore */}
                <InfoWindow
                  position={{
                    lat: selectedStore.latitude,
                    lng: selectedStore.longitude,
                  }}
                  onCloseClick={() => {
                    return setSelectedStore(null)
                  }}>
                  <StoreMapItemInfoWindow
                    store={selectedStore}
                    onClose={() => {
                      return setSelectedStore(null)
                    }}
                  />
                </InfoWindow>
              </div>
            )}
          </StoresMap>
          <div className='absolute left-0 top-6 hidden lg:block'>
            {!!regions?.length && (
              <div className='flex items-center gap-2.5 flex-wrap px-6'>
                {[defaultRegion, ...regions].map((region, index) => {
                  return (
                    <Button
                      key={index}
                      theme={{ root: `${styles.stateButton} rounded-full` }}
                      size='sm'
                      onClick={() => {
                        return handleChangeRegion(region.value)
                      }}>
                      <Typography
                        tag={TypographyTag.span}
                        variant={TypographyVariant.BodySmallBold}>
                        {region.label}
                      </Typography>
                    </Button>
                  )
                })}
              </div>
            )}
          </div>
        </div>
        <div
          className={classNames(
            'mt-6',
            isShowList ? 'block lg:hidden' : 'hidden'
          )}>
          <StoresList stores={filteredStores} />
        </div>
      </div>
    </SectionContainer>
  )
}
